import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  CreditCard,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  DollarSign,
  Link as LinkIcon,
  XCircle,
  Plus,
  Pencil,
  Megaphone,
  X,
  Search,
  ChevronDown,
  ChevronUp,
  Package,
  Globe,
  Coins,
  Filter,
  ShoppingCart,
  Palette,
  Mail,
  Image,
  Type,
} from 'lucide-react';
import tremendousConnectService from '../../services/tremendousConnectService';

/**
 * Tremendous Connect Component
 * Allows admins to connect their organization to Tremendous for reward payouts
 */
const TremendousConnect = ({ organizationId, adminEmail, adminName }) => {
  const [status, setStatus] = useState(null);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const pollingRef = useRef(null);

  // Campaign state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '', description: '', products: [],
    webpage_style: { headline: '', message: '', logo_image_url: '', logo_image_height_px: '', logo_background_color: '', background_color: '' },
    email_style: { sender_name: '', subject_line: '', logo_image_url: '', logo_image_height_px: '', logo_background_color: '', button_color: '' },
  });
  const [campaignSaving, setCampaignSaving] = useState(false);
  const [campaignError, setCampaignError] = useState(null);
  // Products state (for campaign product selection)
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  // Marketplace sync state
  const [syncingCampaignId, setSyncingCampaignId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);

  // Check URL params for OAuth callback results on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('tremendous_connected') === 'true') {
      setError(null);
    }
    if (params.get('tremendous_error')) {
      setError(decodeURIComponent(params.get('tremendous_error')));
    }
    // Clean up URL params
    if (params.get('tremendous_connected') || params.get('tremendous_error') || params.get('tremendous_status')) {
      const url = new URL(window.location);
      url.searchParams.delete('tremendous_connected');
      url.searchParams.delete('tremendous_error');
      url.searchParams.delete('tremendous_status');
      window.history.replaceState({}, '', url);
    }
  }, []);

  // Fetch connection status on mount
  useEffect(() => {
    if (organizationId) {
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  // Note: balance is fetched inside fetchStatus() when connected

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  const startPolling = useCallback(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    setPolling(true);
    pollingRef.current = setInterval(async () => {
      try {
        const data = await tremendousConnectService.getStatus(organizationId);
        setStatus(data);
        // Stop polling once approved or rejected
        if (data.status === 'approved' || data.status === 'rejected') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setPolling(false);
          setConnecting(false);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 5000);
  }, [organizationId]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await tremendousConnectService.getStatus(organizationId);
      setStatus(data);
      // If connected, immediately fetch campaigns and balance
      if (data?.is_connected) {
        fetchCampaigns();
        fetchBalance();
      }
    } catch (err) {
      console.error('Failed to fetch Tremendous status:', err);
      setError('Failed to load connection status');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const data = await tremendousConnectService.getBalance(organizationId);
      setBalance(data);
    } catch (err) {
      console.error('Failed to fetch balance:', err);
    }
  };

  // Note: campaigns are fetched inside fetchStatus() when connected

  const fetchCampaigns = async () => {
    try {
      setCampaignsLoading(true);
      const data = await tremendousConnectService.listCampaigns(organizationId);
      setCampaigns(data.campaigns || []);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const fetchProducts = async () => {
    if (products.length > 0) return; // already loaded
    try {
      setProductsLoading(true);
      const data = await tremendousConnectService.listProducts(organizationId);
      setProducts(data.products || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    } finally {
      setProductsLoading(false);
    }
  };

  const emptyWebpageStyle = { headline: '', message: '', logo_image_url: '', logo_image_height_px: '', logo_background_color: '', background_color: '' };
  const emptyEmailStyle = { sender_name: '', subject_line: '', logo_image_url: '', logo_image_height_px: '', logo_background_color: '', button_color: '' };

  const openCreateCampaign = () => {
    setEditingCampaign(null);
    setCampaignForm({ name: '', description: '', products: [], webpage_style: { ...emptyWebpageStyle }, email_style: { ...emptyEmailStyle }, _showWebpage: true, _showEmail: true });
    setCampaignError(null);
    setProductSearch('');
    setCountryFilter('');
    setCurrencyFilter('');
    setShowProductDropdown(false);
    setShowCampaignModal(true);
    fetchProducts();
  };

  const populateCampaignForm = (campaign) => {
    const ws = campaign.webpage_style || {};
    const es = campaign.email_style || {};
    setCampaignForm({
      name: campaign.name || '',
      description: campaign.description || '',
      products: (campaign.products || []).map(p => typeof p === 'string' ? p : p.id),
      webpage_style: {
        headline: ws.headline || '',
        message: ws.message || '',
        logo_image_url: ws.logo_image_url || '',
        logo_image_height_px: ws.logo_image_height_px || '',
        logo_background_color: ws.logo_background_color || '',
        background_color: ws.background_color || '',
      },
      email_style: {
        sender_name: es.sender_name || '',
        subject_line: es.subject_line || '',
        logo_image_url: es.logo_image_url || '',
        logo_image_height_px: es.logo_image_height_px || '',
        logo_background_color: es.logo_background_color || '',
        button_color: es.button_color || '',
      },
    });
  };

  const openEditCampaign = async (campaign) => {
    setEditingCampaign(campaign);
    // Start with list data, then fetch full campaign details for style fields
    populateCampaignForm(campaign);
    setCampaignError(null);
    setProductSearch('');
    setCountryFilter('');
    setCurrencyFilter('');
    setShowProductDropdown(false);
    setShowCampaignModal(true);
    fetchProducts();

    // Fetch full campaign details (list endpoint may not include styles)
    try {
      const fullData = await tremendousConnectService.getCampaign(organizationId, campaign.id);
      const full = fullData.campaign || fullData;
      setEditingCampaign(full);
      populateCampaignForm(full);
    } catch (err) {
      console.error('Failed to fetch campaign details:', err);
    }
  };

  // Helper: strip empty strings and internal keys from a style object, return undefined if all empty
  const cleanStyle = (style) => {
    const cleaned = {};
    for (const [key, value] of Object.entries(style)) {
      if (key.startsWith('_')) continue;
      if (value !== '' && value !== null && value !== undefined) {
        cleaned[key] = key === 'logo_image_height_px' ? Number(value) : value;
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  };

  const handleSaveCampaign = async () => {
    if (!campaignForm.name.trim()) {
      setCampaignError('Campaign name is required');
      return;
    }
    try {
      setCampaignSaving(true);
      setCampaignError(null);
      const payload = {
        name: campaignForm.name.trim(),
        description: campaignForm.description.trim() || undefined,
        products: campaignForm.products.length > 0 ? campaignForm.products : undefined,
        webpage_style: cleanStyle(campaignForm.webpage_style),
        email_style: cleanStyle(campaignForm.email_style),
      };
      if (editingCampaign) {
        await tremendousConnectService.updateCampaign(organizationId, editingCampaign.id, payload);
      } else {
        await tremendousConnectService.createCampaign(organizationId, payload);
      }
      setShowCampaignModal(false);
      fetchCampaigns();
    } catch (err) {
      console.error('Failed to save campaign:', err);
      setCampaignError(err.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setCampaignSaving(false);
    }
  };

  const toggleProduct = (productId) => {
    setCampaignForm(prev => ({
      ...prev,
      products: prev.products.includes(productId)
        ? prev.products.filter(id => id !== productId)
        : [...prev.products, productId],
    }));
  };

  // Derive unique countries and currencies from loaded products
  const availableCountries = [...new Set(
    products.flatMap(p => (p.countries || []).map(c => c.abbr)).filter(Boolean)
  )].sort();
  const availableCurrencies = [...new Set(
    products.flatMap(p => p.currency_codes || []).filter(Boolean)
  )].sort();

  const filteredProducts = products.filter(p => {
    const matchesSearch = (p.name || '').toLowerCase().includes(productSearch.toLowerCase());
    const matchesCountry = !countryFilter || (p.countries || []).some(c => c.abbr === countryFilter);
    const matchesCurrency = !currencyFilter || (p.currency_codes || []).includes(currencyFilter);
    return matchesSearch && matchesCountry && matchesCurrency;
  });

  const handleSyncToMarketplace = async (campaignId) => {
    try {
      setSyncingCampaignId(campaignId);
      setSyncResult(null);
      const result = await tremendousConnectService.syncCampaignToMarketplace(organizationId, campaignId);
      setSyncResult({
        campaignId,
        ...result,
      });
    } catch (err) {
      console.error('Failed to sync campaign:', err);
      setError(err.response?.data?.detail || 'Failed to sync campaign to marketplace');
    } finally {
      setSyncingCampaignId(null);
    }
  };

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);

      const result = await tremendousConnectService.startConnect(
        organizationId,
        adminEmail,
        adminName,
        window.location.href
      );

      // Open Tremendous onboarding in a new tab so the user stays in the app
      if (result.session_url) {
        window.open(result.session_url, '_blank');
        // Start polling for status changes while user completes onboarding
        startPolling();
      }
    } catch (err) {
      console.error('Failed to start Tremendous Connect:', err);
      setError(err.response?.data?.detail || 'Failed to start connection');
      setConnecting(false);
    }
  };

  const handleOpenDashboard = async () => {
    try {
      setError(null);
      const result = await tremendousConnectService.getDashboardSession(organizationId);

      if (result.session_url) {
        window.open(result.session_url, '_blank');
      }
    } catch (err) {
      console.error('Failed to open dashboard:', err);
      setError(err.response?.data?.detail || 'Failed to open dashboard');
    }
  };

  const getStatusBadge = () => {
    if (!status) return null;

    const statusConfig = {
      not_connected: {
        color: 'bg-gray-100 text-gray-700',
        icon: <LinkIcon className="w-4 h-4" />,
        text: 'Not Connected',
      },
      pending: {
        color: 'bg-yellow-100 text-yellow-700',
        icon: <Clock className="w-4 h-4" />,
        text: 'Pending',
      },
      registered: {
        color: 'bg-blue-100 text-blue-700',
        icon: <Clock className="w-4 h-4" />,
        text: 'Registered - Awaiting Approval',
      },
      approved: {
        color: 'bg-green-100 text-green-700',
        icon: <CheckCircle className="w-4 h-4" />,
        text: 'Connected',
      },
      rejected: {
        color: 'bg-red-100 text-red-700',
        icon: <XCircle className="w-4 h-4" />,
        text: 'Rejected',
      },
    };

    const config = statusConfig[status.status] || statusConfig.not_connected;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.icon}
        {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          <span className="ml-2 text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-2xl">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Tremendous Connect</h3>
              <p className="text-sm text-white/80">Manage reward payouts</p>
            </div>
          </div>
          {getStatusBadge()}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="text-sm text-red-600">{error}</p>
            </div>
          </div>
        )}

        {/* Not Connected State */}
        {(!status || status.status === 'not_connected') && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-indigo-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Connect to Tremendous
            </h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Connect your organization to Tremendous to enable reward payouts.
              You'll set up your own funding source and manage your reward budget.
            </p>
            <button
              onClick={handleConnect}
              disabled={connecting}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-medium rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {connecting ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Opening Tremendous...
                </>
              ) : (
                <>
                  <LinkIcon className="w-5 h-5" />
                  Connect Now
                </>
              )}
            </button>
          </div>
        )}

        {/* Pending/Registered State */}
        {(status?.status === 'pending' || status?.status === 'registered') && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Connection Pending
            </h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {polling
                ? 'Complete the onboarding in the Tremendous tab. This page will update automatically when done.'
                : 'Your connection is being processed. Complete the onboarding in Tremendous to finish setup.'}
            </p>
            {polling && (
              <div className="flex items-center justify-center gap-2 mb-4 text-indigo-600">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm font-medium">Waiting for onboarding to complete...</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleConnect}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-full hover:bg-indigo-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                {polling ? 'Reopen Tremendous' : 'Continue Setup'}
              </button>
              <button
                onClick={fetchStatus}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-full hover:bg-gray-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Status
              </button>
            </div>
          </div>
        )}

        {/* Connected State */}
        {status?.is_connected && (
          <div className="space-y-6">
            {/* Balance Card */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700 mb-1">Available Balance</p>
                  <p className="text-3xl font-bold text-green-800">
                    {balance ? `$${balance.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '---'}
                  </p>
                  <p className="text-sm text-green-600 mt-1">{balance?.currency || 'USD'}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <DollarSign className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleOpenDashboard}
                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-full hover:bg-indigo-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Open Tremendous Dashboard
              </button>
              <button
                onClick={fetchBalance}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-full hover:bg-gray-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Balance
              </button>
            </div>

            {/* Campaigns Section */}
            <div className="border border-gray-200 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-indigo-600" />
                  <h4 className="text-base font-semibold text-gray-900">Campaigns</h4>
                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
                    {campaigns.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={fetchCampaigns}
                    disabled={campaignsLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white text-gray-700 font-medium rounded-full border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${campaignsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                  <button
                    onClick={openCreateCampaign}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white font-medium rounded-full hover:bg-indigo-700 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    New Campaign
                  </button>
                </div>
              </div>

              {/* Sync result banner */}
              {syncResult && (
                <div className="px-5 py-3 bg-green-50 border-b border-green-200 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-green-800">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>
                      <strong>{syncResult.campaign_name}</strong> synced to marketplace —{' '}
                      {syncResult.items_created || 0} added, {syncResult.items_updated || 0} updated, {syncResult.items_deleted || 0} removed
                      ({syncResult.total_items || 0} total products)
                    </span>
                  </div>
                  <button onClick={() => setSyncResult(null)} className="text-green-600 hover:text-green-800">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {campaignsLoading && campaigns.length === 0 ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />
                  <span className="ml-2 text-sm text-gray-500">Loading campaigns...</span>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <Megaphone className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No campaigns yet</p>
                  <p className="text-xs text-gray-400 mt-1">Create a campaign to organize your rewards</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/50">
                        <th className="text-left px-5 py-3 font-medium text-gray-600">Name</th>
                        <th className="text-left px-5 py-3 font-medium text-gray-600">Description</th>
                        <th className="text-center px-5 py-3 font-medium text-gray-600">Products</th>
                        <th className="text-right px-5 py-3 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {campaigns.map((campaign) => {
                        const isSyncing = syncingCampaignId === campaign.id;
                        const isSynced = syncResult?.campaignId === campaign.id;
                        return (
                          <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-3">
                              <span className="font-medium text-gray-900">{campaign.name}</span>
                            </td>
                            <td className="px-5 py-3 text-gray-600 max-w-xs truncate">
                              {campaign.description || <span className="text-gray-400 italic">No description</span>}
                            </td>
                            <td className="px-5 py-3 text-center">
                              <span className="inline-flex items-center gap-1 text-gray-600">
                                <Package className="w-3.5 h-3.5" />
                                {Array.isArray(campaign.products) ? campaign.products.length : 0}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleSyncToMarketplace(campaign.id)}
                                  disabled={isSyncing}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                                    isSynced
                                      ? 'bg-green-50 text-green-700 border-green-300'
                                      : 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100'
                                  } disabled:opacity-50`}
                                >
                                  {isSyncing ? (
                                    <><RefreshCw className="w-3 h-3 animate-spin" /> Syncing...</>
                                  ) : isSynced ? (
                                    <><CheckCircle className="w-3 h-3" /> Synced</>
                                  ) : (
                                    <><ShoppingCart className="w-3 h-3" /> Sync to Marketplace</>
                                  )}
                                </button>
                                <button
                                  onClick={() => openEditCampaign(campaign)}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white text-gray-700 font-medium rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
                                >
                                  <Pencil className="w-3 h-3" />
                                  Edit
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Manage Your Rewards</p>
                  <p className="text-sm text-blue-600 mt-1">
                    Use the Tremendous dashboard to add funds, view transaction history,
                    and manage your reward settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Rejected State */}
        {status?.status === 'rejected' && (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              Connection Rejected
            </h4>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Your Tremendous Connect application was not approved.
              Please contact support for more information.
            </p>
            <button
              onClick={handleConnect}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white font-medium rounded-full hover:bg-indigo-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Campaign Create/Edit Modal */}
      {showCampaignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCampaign ? 'Edit Campaign' : 'New Campaign'}
              </h3>
              <button
                onClick={() => setShowCampaignModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {campaignError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-2xl text-sm text-red-700">
                  {campaignError}
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Q1 Employee Rewards"
                  className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description for this campaign"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
                />
              </div>

              {/* Products */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Products
                  <span className="ml-1 text-gray-400 font-normal">
                    ({campaignForm.products.length} selected)
                  </span>
                </label>

                {/* Selected products chips */}
                {campaignForm.products.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {campaignForm.products.map(pid => {
                      const prod = products.find(p => p.id === pid);
                      return (
                        <span
                          key={pid}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-2xl border border-indigo-200"
                        >
                          {prod?.name || pid}
                          <button
                            onClick={() => toggleProduct(pid)}
                            className="hover:text-indigo-900"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Country & Currency filters */}
                <div className="flex gap-2 mb-2">
                  <div className="flex-1 relative">
                    <Globe className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <select
                      value={countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-full bg-white outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
                    >
                      <option value="">All Countries</option>
                      {availableCountries.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                  <div className="flex-1 relative">
                    <Coins className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <select
                      value={currencyFilter}
                      onChange={(e) => setCurrencyFilter(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-full bg-white outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 appearance-none"
                    >
                      <option value="">All Currencies</option>
                      {availableCurrencies.map(code => (
                        <option key={code} value={code}>{code}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                {/* Active filters indicator */}
                {(countryFilter || currencyFilter) && (
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    <Filter className="w-3 h-3 text-indigo-500" />
                    <span className="text-gray-500">
                      Showing {filteredProducts.length} of {products.length} products
                    </span>
                    <button
                      onClick={() => { setCountryFilter(''); setCurrencyFilter(''); }}
                      className="text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Clear filters
                    </button>
                  </div>
                )}

                {/* Product search & dropdown */}
                <div className="relative">
                  <div
                    className="flex items-center border border-gray-300 rounded-2xl overflow-hidden cursor-pointer"
                    onClick={() => { setShowProductDropdown(!showProductDropdown); fetchProducts(); }}
                  >
                    <Search className="w-4 h-4 text-gray-400 ml-3" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); fetchProducts(); }}
                      onClick={(e) => e.stopPropagation()}
                      placeholder="Search products by name..."
                      className="w-full px-2 py-2 text-sm outline-none"
                    />
                    {showProductDropdown ? (
                      <ChevronUp className="w-4 h-4 text-gray-400 mr-3" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 mr-3" />
                    )}
                  </div>

                  {showProductDropdown && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-2xl shadow-lg max-h-56 overflow-y-auto">
                      {productsLoading ? (
                        <div className="flex items-center justify-center py-4">
                          <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
                          <span className="ml-2 text-xs text-gray-500">Loading products...</span>
                        </div>
                      ) : filteredProducts.length === 0 ? (
                        <div className="text-center py-4 text-xs text-gray-500">
                          {productSearch || countryFilter || currencyFilter
                            ? 'No products match your filters'
                            : 'No products available'}
                        </div>
                      ) : (
                        filteredProducts.slice(0, 50).map(product => {
                          const isSelected = campaignForm.products.includes(product.id);
                          const countries = (product.countries || []).map(c => c.abbr).join(', ');
                          const currencies = (product.currency_codes || []).join(', ');
                          return (
                            <div
                              key={product.id}
                              onClick={() => toggleProduct(product.id)}
                              className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors ${
                                isSelected ? 'bg-indigo-50' : ''
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                readOnly
                                className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 flex-shrink-0"
                              />
                              <div className="flex-1 min-w-0">
                                <span className={`block truncate ${isSelected ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>
                                  {product.name}
                                </span>
                                <span className="block text-xs text-gray-400 truncate">
                                  {countries && <><Globe className="w-3 h-3 inline mr-0.5" />{countries}</>}
                                  {countries && currencies && ' · '}
                                  {currencies && <><Coins className="w-3 h-3 inline mr-0.5" />{currencies}</>}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      )}
                      {filteredProducts.length > 50 && (
                        <div className="text-center py-2 text-xs text-gray-400 border-t border-gray-100">
                          Showing 50 of {filteredProducts.length} — refine your search
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Webpage Style */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCampaignForm(prev => ({ ...prev, _showWebpage: !prev._showWebpage }))}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-700">Webpage Style</span>
                  </div>
                  {campaignForm._showWebpage ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {campaignForm._showWebpage && (
                  <div className="px-4 py-3 space-y-3 border-t border-gray-200">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Headline</label>
                      <input
                        type="text"
                        value={campaignForm.webpage_style.headline}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, headline: e.target.value } }))}
                        placeholder='e.g. "You received a reward!"'
                        className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                      <textarea
                        value={campaignForm.webpage_style.message}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, message: e.target.value } }))}
                        placeholder="Message shown on the reward page"
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Logo Image URL</label>
                      <input
                        type="url"
                        value={campaignForm.webpage_style.logo_image_url}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, logo_image_url: e.target.value } }))}
                        placeholder="https://example.com/logo.png"
                        className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Logo Height (px)</label>
                        <input
                          type="number"
                          value={campaignForm.webpage_style.logo_image_height_px}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, logo_image_height_px: e.target.value } }))}
                          placeholder="48"
                          className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Logo BG Color</label>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={campaignForm.webpage_style.logo_background_color}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, logo_background_color: e.target.value } }))}
                            placeholder="#ffffff"
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                          />
                          <input
                            type="color"
                            value={campaignForm.webpage_style.logo_background_color || '#ffffff'}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, logo_background_color: e.target.value } }))}
                            className="w-9 h-9 rounded border border-gray-300 cursor-pointer p-0.5"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Background Color</label>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={campaignForm.webpage_style.background_color}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, background_color: e.target.value } }))}
                            placeholder="#4f46e5"
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                          />
                          <input
                            type="color"
                            value={campaignForm.webpage_style.background_color || '#4f46e5'}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, webpage_style: { ...prev.webpage_style, background_color: e.target.value } }))}
                            className="w-9 h-9 rounded border border-gray-300 cursor-pointer p-0.5"
                          />
                        </div>
                      </div>
                    </div>
                    {campaignForm.webpage_style.logo_image_url && (
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-2xl">
                        <Image className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <img
                          src={campaignForm.webpage_style.logo_image_url}
                          alt="Logo preview"
                          className="h-8 max-w-[120px] object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="text-xs text-gray-400">Preview</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Email Style */}
              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCampaignForm(prev => ({ ...prev, _showEmail: !prev._showEmail }))}
                  className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-medium text-gray-700">Email Style</span>
                  </div>
                  {campaignForm._showEmail ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {campaignForm._showEmail && (
                  <div className="px-4 py-3 space-y-3 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Sender Name</label>
                        <input
                          type="text"
                          value={campaignForm.email_style.sender_name}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, sender_name: e.target.value } }))}
                          placeholder="e.g. Your Company"
                          className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Subject Line</label>
                        <input
                          type="text"
                          value={campaignForm.email_style.subject_line}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, subject_line: e.target.value } }))}
                          placeholder="e.g. You've received a reward!"
                          className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Logo Image URL</label>
                      <input
                        type="url"
                        value={campaignForm.email_style.logo_image_url}
                        onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, logo_image_url: e.target.value } }))}
                        placeholder="https://example.com/email-logo.png"
                        className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Logo Height (px)</label>
                        <input
                          type="number"
                          value={campaignForm.email_style.logo_image_height_px}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, logo_image_height_px: e.target.value } }))}
                          placeholder="48"
                          className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Logo BG Color</label>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={campaignForm.email_style.logo_background_color}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, logo_background_color: e.target.value } }))}
                            placeholder="#ffffff"
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                          />
                          <input
                            type="color"
                            value={campaignForm.email_style.logo_background_color || '#ffffff'}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, logo_background_color: e.target.value } }))}
                            className="w-9 h-9 rounded border border-gray-300 cursor-pointer p-0.5"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Button Color</label>
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={campaignForm.email_style.button_color}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, button_color: e.target.value } }))}
                            placeholder="#4f46e5"
                            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-full text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                          />
                          <input
                            type="color"
                            value={campaignForm.email_style.button_color || '#4f46e5'}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, email_style: { ...prev.email_style, button_color: e.target.value } }))}
                            className="w-9 h-9 rounded border border-gray-300 cursor-pointer p-0.5"
                          />
                        </div>
                      </div>
                    </div>
                    {campaignForm.email_style.logo_image_url && (
                      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-2xl">
                        <Image className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <img
                          src={campaignForm.email_style.logo_image_url}
                          alt="Email logo preview"
                          className="h-8 max-w-[120px] object-contain"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <span className="text-xs text-gray-400">Preview</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowCampaignModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCampaign}
                disabled={campaignSaving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {campaignSaving ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : editingCampaign ? (
                  'Update Campaign'
                ) : (
                  'Create Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default TremendousConnect;
